/**
 * Lenavio Learn — admin dashboard.
 *
 * Top-line completion + overdue counters, status breakdown, per-
 * department completion rates, and the compliance report (red /
 * yellow / green per required course + drilldown of overdue
 * employees per course).
 *
 * HR_ADMIN+ only — gated upstream by RoleGate at the route level.
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, BarChart3, GraduationCap, ShieldCheck } from "lucide-react";

import {
  getComplianceReport,
  getDashboard,
  type ComplianceReport,
  type TrainingDashboard,
} from "@/api/training";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import { PageHeader } from "@/components/layout/PageHeader";
import { pageTransition } from "@/lib/utils";

export function TrainingDashboardPage() {
  const navigate = useNavigate();
  const [dashboard, setDashboard] = useState<TrainingDashboard | null>(null);
  const [compliance, setCompliance] = useState<ComplianceReport | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    Promise.all([getDashboard(), getComplianceReport()])
      .then(([d, c]) => {
        if (cancelled) return;
        setDashboard(d);
        setCompliance(c);
      })
      .catch(() => {
        if (!cancelled) setError("Could not load training dashboard.");
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (error) {
    return (
      <Card className="border-danger/40 bg-danger/5">
        <p className="text-sm text-danger">{error}</p>
      </Card>
    );
  }
  if (!dashboard || !compliance) {
    return (
      <div className="space-y-3">
        {[0, 1, 2].map((i) => (
          <div key={i} className="h-32 rounded bg-border/30 animate-pulse" />
        ))}
      </div>
    );
  }

  const completionPct = Math.round(dashboard.completion_rate * 100);

  return (
    <motion.div {...pageTransition}>
      <PageHeader
        title="Training Dashboard"
        subtitle="Completion rates, overdue alerts, and compliance health."
        actions={
          <Button variant="ghost" size="sm" onClick={() => navigate("/training")}>
            <ArrowLeft className="h-3.5 w-3.5" />
            Library
          </Button>
        }
      />

      {/* Top metrics */}
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-3 mb-4">
        <MetricCard
          icon={<GraduationCap className="h-5 w-5 text-accent" />}
          label="Active courses"
          value={`${dashboard.total_courses}`}
        />
        <MetricCard
          icon={<BarChart3 className="h-5 w-5 text-accent" />}
          label="Completion rate"
          value={`${completionPct}%`}
          hint={`${dashboard.breakdown.completed} of ${
            dashboard.breakdown.completed + dashboard.breakdown.overdue
          } settled`}
        />
        <MetricCard
          icon={<ShieldCheck className="h-5 w-5 text-warning" />}
          label="Active assignments"
          value={`${dashboard.active_assignments}`}
          hint={`${dashboard.breakdown.in_progress} in progress`}
        />
        <MetricCard
          icon={<ShieldCheck className="h-5 w-5 text-danger" />}
          label="Overdue"
          value={`${dashboard.overdue_count}`}
          hint={
            dashboard.overdue_count === 0
              ? "All caught up"
              : "Needs immediate attention"
          }
        />
      </div>

      {/* Compliance health */}
      <Card className="mb-4">
        <h3 className="text-sm font-semibold uppercase tracking-wide text-text-muted mb-3">
          Required-training health
        </h3>
        {compliance.courses.length === 0 ? (
          <p className="text-sm text-text-muted">
            No required courses defined yet. Mark a course as required from
            the library to track it here.
          </p>
        ) : (
          <ul className="divide-y divide-border">
            {compliance.courses.map((c) => {
              const pct = Math.round(c.completion_rate * 100);
              const color =
                c.health === "green"
                  ? "bg-success"
                  : c.health === "yellow"
                    ? "bg-amber-400"
                    : "bg-danger";
              return (
                <li
                  key={c.course_id}
                  className="flex items-center justify-between py-3 gap-3"
                >
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <span className={`h-2.5 w-2.5 rounded-full ${color}`} />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-text-primary truncate">
                        {c.title}
                      </p>
                      <p className="text-xs text-text-muted">
                        {c.completed} / {c.eligible_employees} completed · {pct}% ·{" "}
                        {c.overdue} overdue · {c.recurrence}
                      </p>
                    </div>
                  </div>
                  <div className="hidden sm:block w-32 h-1.5 rounded-full bg-border/60 overflow-hidden">
                    <div
                      className={`h-1.5 rounded-full ${color}`}
                      style={{ width: `${Math.max(2, pct)}%` }}
                    />
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </Card>

      {/* Department breakdown */}
      <Card className="mb-4">
        <h3 className="text-sm font-semibold uppercase tracking-wide text-text-muted mb-3">
          Engagement by department
        </h3>
        {dashboard.departments.length === 0 ? (
          <p className="text-sm text-text-muted">
            Department breakdown shows once employees have assignments.
          </p>
        ) : (
          <ul className="divide-y divide-border">
            {dashboard.departments.map((d) => (
              <li
                key={d.department_name + (d.department_id ?? "none")}
                className="flex items-center justify-between py-2.5 gap-3"
              >
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-text-primary truncate">
                    {d.department_name}
                  </p>
                  <p className="text-xs text-text-muted">
                    {d.employees} employee{d.employees === 1 ? "" : "s"} ·{" "}
                    {d.completed} completed · {d.overdue} overdue
                  </p>
                </div>
                <Badge variant="info" className="min-w-[3.5rem] text-center">
                  {Math.round(d.completion_rate * 100)}%
                </Badge>
              </li>
            ))}
          </ul>
        )}
      </Card>

      {/* Overdue drill-down */}
      {compliance.overdue_employees.length > 0 && (
        <Card>
          <h3 className="text-sm font-semibold uppercase tracking-wide text-text-muted mb-3">
            Overdue employees
          </h3>
          <ul className="divide-y divide-border max-h-80 overflow-y-auto">
            {compliance.overdue_employees.map((row, i) => (
              <li
                key={`${row.employee_id}-${i}`}
                className="flex items-center justify-between py-2.5 gap-3"
              >
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-text-primary truncate">
                    {row.name}
                  </p>
                  <p className="text-xs text-text-muted">
                    {row.course_title} · due {row.due_date || "—"}
                  </p>
                </div>
                <Badge variant="danger">Overdue</Badge>
              </li>
            ))}
          </ul>
        </Card>
      )}
    </motion.div>
  );
}


function MetricCard({
  icon,
  label,
  value,
  hint,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  hint?: string;
}) {
  return (
    <Card>
      <div className="flex items-center gap-2 mb-1">
        {icon}
        <p className="text-xs uppercase tracking-wide text-text-muted">{label}</p>
      </div>
      <p className="text-2xl font-semibold font-heading text-text-primary">
        {value}
      </p>
      {hint && <p className="text-xs text-text-muted mt-0.5">{hint}</p>}
    </Card>
  );
}
